// OrderModifier and OrderModifierGroup nest each other for multi-level
// modifiers, so the two modules import each other. This is safe: each only
// references the other's *class* inside method bodies (constructor / fromJSON),
// never at module-evaluation time — by the time those run both modules are
// fully initialized. OrderModifierGroup imports back the same way.
import { IOrderModifierGroup, IOrderModifierGroupData, OrderModifierGroup } from './OrderModifierGroup';

export interface IOrderModifier {
    readonly modifierId: number;
    readonly modifierGuid?: string;
    /**
     * The marketplace's own reference for this selection when it is a SIZE rung
     * rather than a modifier — see the class field for why `modifierId` cannot
     * carry it.
     */
    readonly externalSizeKey?: string;

    readonly name: string;
    readonly priceDelta: number;
    /**
     * How many instances of this modifier the customer selected (e.g.
     * "Extra Bacon ×2" → 2). Defaults to 1. Carried from the cart so the
     * RecipeOps BOM resolver can scale modifier ingredient draws by it at
     * order close (modifier_qty × selectedQuantity × orderQty).
     */
    readonly selectedQuantity?: number;
    /**
     * Nested modifier groups (modifier-of-modifier). Uber Eats allows
     * `selected_modifier_groups[].selected_items[].selected_modifier_groups[]`
     * (e.g. "Choose sauce" sub-group under a "burger patty" modifier). Empty /
     * absent for providers that don't support nesting. Uses `import type` to
     * break the circular reference at the type level.
     */
    readonly modifierGroups?: IOrderModifierGroup[];
}

export interface IOrderModifierData {
    modifierId: number;
    modifierGuid?: string;
    /** See {@link IOrderModifier.externalSizeKey}. */
    externalSizeKey?: string;
    name: string;
    priceDelta?: number;
    selectedQuantity?: number;
    /** Nested modifier groups for multi-level modifiers. */
    modifierGroups?: IOrderModifierGroupData[];
}

export class OrderModifier implements IOrderModifier {
    readonly modifierId: number;
    readonly modifierGuid?: string;
    readonly name: string;
    readonly priceDelta: number;
    readonly selectedQuantity: number;
    /**
     * The marketplace's own reference for this selection when it is a SIZE rung
     * rather than a modifier.
     *
     * 🔴 Preserved because `modifierId` CANNOT carry it. A size is not in the
     * modifier catalog, so ingest falls back to a POSITIONAL id (`index + 1`) —
     * and `modifierLookup` is keyed by `modifierId.toString()`, so the size then
     * "resolves" to modifier #1, #2 … and validation reports a confident answer
     * about an unrelated modifier's availability.
     *
     * Holds the size's guid, or its name for a menu published before size guids
     * existed; `findSizeByExternalKey` resolves either against the parent item's
     * own `sizePrice`.
     */
    readonly externalSizeKey?: string;
    readonly modifierGroups?: IOrderModifierGroup[];

    constructor(data: IOrderModifierData) {
        this.modifierId = data.modifierId;
        this.modifierGuid = data.modifierGuid;
        this.externalSizeKey = data.externalSizeKey;
        this.name = data.name;
        this.priceDelta = data.priceDelta ?? 0;
        this.selectedQuantity = data.selectedQuantity ?? 1;
        if (data.modifierGroups && data.modifierGroups.length > 0) {
            this.modifierGroups = data.modifierGroups.map(
                (g) => new OrderModifierGroup(g as any),
            );
        }
    }

    toJSON(): IOrderModifier {
        return {
            modifierId: this.modifierId,
            modifierGuid: this.modifierGuid,
            externalSizeKey: this.externalSizeKey,
            name: this.name,
            priceDelta: this.priceDelta,
            selectedQuantity: this.selectedQuantity,
            modifierGroups: this.modifierGroups?.map((g) => (g as any).toJSON()),
        };
    }

    static fromJSON(data: Record<string, unknown>): OrderModifier {
        if (typeof data.modifierId !== 'number') {
            throw new Error("OrderModifier.fromJSON: modifierId must be a number");
        }
        if (typeof data.name !== 'string') {
            throw new Error("OrderModifier.fromJSON: name must be a string");
        }

        let nestedGroups: IOrderModifierGroupData[] | undefined;
        if (Array.isArray(data.modifierGroups) && data.modifierGroups.length > 0) {
            nestedGroups = (data.modifierGroups as Record<string, unknown>[]).map(
                (g) => OrderModifierGroup.fromJSON(g),
            );
        }

        return new OrderModifier({
            modifierId: data.modifierId,
            modifierGuid: data.modifierGuid as string | undefined,
            externalSizeKey: data.externalSizeKey as string | undefined,
            name: data.name,
            priceDelta: typeof data.priceDelta === 'number' ? data.priceDelta : 0,
            selectedQuantity:
                typeof data.selectedQuantity === 'number' ? data.selectedQuantity : 1,
            modifierGroups: nestedGroups,
        });
    }
}
